import { ApplyToProjectRequest } from '@internal/types/api';
import { AvailabilityHours } from '@internal/types/profile';
import {
  Project,
  ProjectAnswer,
  ProjectQuestion,
} from '@internal/types/projects';

import type { SchemaType } from './apply-form';

export const MIN_RATE = 1;
export const MAX_RATE = 300;

export const FULL_TIME_WEEKLY_INVOLVEMENT: AvailabilityHours = '40h';

export const requiresFullTimeAgreement = (project: Project): boolean =>
  project.projectWeeklyInvolvement === FULL_TIME_WEEKLY_INVOLVEMENT;

export const expectsRateExpectations = (
  project: Project,
  isPartnerAccount: boolean
): boolean => !isPartnerAccount && !(Number(project.candidateRatePerHour) > 0);

const parseRate = (value?: string): number | undefined =>
  value?.trim() ? Number(value) : undefined;

export const prepareFormData = ({
  hubspotProjectId,
  contactId,
  formData,
  withRateExpectations,
}: {
  hubspotProjectId: string;
  contactId: string;
  formData: SchemaType;
  withRateExpectations: boolean;
}): Omit<ApplyToProjectRequest, 'apiKey'> => ({
  contactId,
  hubspotProjectId,
  ...(withRateExpectations && {
    desiredRateMin: parseRate(formData.basicDetails.minRate),
    desiredRateMax: parseRate(formData.basicDetails.maxRate),
    candidateRateComment:
      formData.basicDetails.rateComment?.trim() || undefined,
  }),
  readyToStartAt: formData.basicDetails.preferredStartDate,
  vacationPlans: formData.basicDetails.vacationPlans,
  interviewTimeslots: formData.basicDetails.timeslots,
  workingHoursFrom: formData.workingHours.startTime,
  workingHoursTo: formData.workingHours.endTime,
  timeZone: formData.workingHours.timeZone,
  candidateApplicationComment: formData.additionalComments,
  projectAnswers: formData.questions.map(question => ({
    hubspotProjectQuestionId: question.id,
    text: question.answer,
  })),
});

export const errorFieldNames = (errors: object, prefix = ''): string[] =>
  Object.entries(errors).flatMap(([key, value]) => {
    if (!value || typeof value !== 'object') return [];
    const name = prefix ? `${prefix}.${key}` : key;
    return 'type' in value && typeof value.type === 'string'
      ? [name]
      : errorFieldNames(value, name);
  });

export const getQuestionsToDisplay = ({
  questions,
  answers,
  isApplicationFormSubmitted,
}: {
  questions: ProjectQuestion[];
  answers: ProjectAnswer[];
  isApplicationFormSubmitted?: boolean;
}) => {
  if (isApplicationFormSubmitted) {
    return answers.map<ProjectQuestion>(answer => ({
      id: answer.hubspotProjectQuestionId as string,
      title: answer.originalQuestionTitle as string,
    }));
  }

  return questions;
};
